/**
 * Block Properties Manager (BPM) reducer helpers — Phase N.5.
 *
 *   - geometry/rename-block-parameter
 *   - geometry/update-block-parameter-description
 *   - geometry/toggle-parameter-show-in-palette
 *   - geometry/reorder-block-parameters
 *   - geometry/rename-block-action
 *   - geometry/update-block-action-description
 *   - geometry/reorder-block-actions
 *
 * Unlike `dynamicAuthoringReducerHelpers.ts`, these handlers do NOT enforce
 * BEDIT context — the Block Properties Manager dialog is intended to work
 * outside an active block-edit session (the user can audit and tweak any
 * block in the drawing without first entering BEDIT). The mutation layer
 * still validates the target block exists and the metadata stays
 * structurally / semantically valid.
 */

import {
  assembleDrawingDocument,
  type DrawingDocument,
} from "@dxf-ai-editor/drawing-model";
import {
  renameBlockActionMutation,
  renameBlockParameterMutation,
  reorderBlockActionsMutation,
  reorderBlockParametersMutation,
  toggleBlockParameterShowInPaletteMutation,
  updateBlockActionDescriptionMutation,
  updateBlockParameterDescriptionMutation,
} from "@dxf-ai-editor/command-engine";
import type { EditorCommand } from "@dxf-ai-editor/contracts";

type RenameBlockParameterCommand = Extract<
  EditorCommand,
  { type: "geometry/rename-block-parameter" }
>;
type UpdateBlockParameterDescriptionCommand = Extract<
  EditorCommand,
  { type: "geometry/update-block-parameter-description" }
>;
type ToggleParameterShowInPaletteCommand = Extract<
  EditorCommand,
  { type: "geometry/toggle-parameter-show-in-palette" }
>;
type ReorderBlockParametersCommand = Extract<
  EditorCommand,
  { type: "geometry/reorder-block-parameters" }
>;
type RenameBlockActionCommand = Extract<
  EditorCommand,
  { type: "geometry/rename-block-action" }
>;
type UpdateBlockActionDescriptionCommand = Extract<
  EditorCommand,
  { type: "geometry/update-block-action-description" }
>;
type ReorderBlockActionsCommand = Extract<
  EditorCommand,
  { type: "geometry/reorder-block-actions" }
>;

export function handleRenameBlockParameter(
  document: DrawingDocument,
  command: RenameBlockParameterCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = renameBlockParameterMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      newName: command.newName,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateBlockParameterDescription(
  document: DrawingDocument,
  command: UpdateBlockParameterDescriptionCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = updateBlockParameterDescriptionMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
      description: command.description,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleToggleParameterShowInPalette(
  document: DrawingDocument,
  command: ToggleParameterShowInPaletteCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = toggleBlockParameterShowInPaletteMutation({
      document,
      blockName: command.blockName,
      parameterId: command.parameterId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleReorderBlockParameters(
  document: DrawingDocument,
  command: ReorderBlockParametersCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = reorderBlockParametersMutation({
      document,
      blockName: command.blockName,
      newOrder: command.newOrder,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleRenameBlockAction(
  document: DrawingDocument,
  command: RenameBlockActionCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = renameBlockActionMutation({
      document,
      blockName: command.blockName,
      actionId: command.actionId,
      newName: command.newName,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateBlockActionDescription(
  document: DrawingDocument,
  command: UpdateBlockActionDescriptionCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = updateBlockActionDescriptionMutation({
      document,
      blockName: command.blockName,
      actionId: command.actionId,
      description: command.description,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleReorderBlockActions(
  document: DrawingDocument,
  command: ReorderBlockActionsCommand,
): DrawingDocument {
  try {
    const { updatedDocument } = reorderBlockActionsMutation({
      document,
      blockName: command.blockName,
      newOrder: command.newOrder,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}
